import type { DisplayTemplate, DisplayTemplateAst, RefNode, TextNode } from '#layers/data-model/types/display-template';
import type { M2MStructure } from '#layers/data-model/types/relation';
import type { GlobalCollectionId, GlobalFieldId } from '#layers/data-model/types/schema/ids';
import type { AnyItem } from '#layers/data-model/types/schema/item';
import type { AnyResolvedField, ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField } from '#layers/data-model/types/schema/resolved-schema';
import type { CollectionFieldPath } from '#layers/data-model/utils/collection-field-path';
import type { QuerySchemaOutput, QuerySchemaWalker } from '#layers/data-model/utils/schema/query-schema';
import type { CollectionRead } from '#layers/data-model/utils/schema/read-output';
import { ensureArray, get, isNil, isNullishOrEmpty } from '#layers/base/public';
import { parseBlockExpression } from '#layers/block-engine/public';
import { DisplayTemplateAstType } from '#layers/data-model/types/display-template';
import { findCollectionFieldById } from '#layers/data-model/utils/fields/fields';
import { getCollectionPrimaryKeyFields, isSingleRelationField } from '#layers/data-model/utils/schema/collection';
import { isPrimitiveTypeString } from '#layers/data-model/utils/schema/primitive-type';
import { outputReturnsFieldPath, resolveReadOutput } from '#layers/data-model/utils/schema/read-output';

export interface DisplayTemplateRenderTextPlanNode {
	type: typeof DisplayTemplateAstType.Text;
	text: string;
}

export interface DisplayTemplateRenderRefPlanNode {
	type: typeof DisplayTemplateAstType.Ref;
	fieldId: GlobalFieldId;
	valuePath: string[];
	leafItemPath: string[];
}

export type DisplayTemplateRenderPlanNode = DisplayTemplateRenderTextPlanNode | DisplayTemplateRenderRefPlanNode;

export interface DisplayTemplateRenderPlan {
	collectionId: GlobalCollectionId;
	nodes: DisplayTemplateRenderPlanNode[];
}

export type ResolvedDisplayTemplateRenderNode =
	| { text: string }
	| { fieldId: GlobalFieldId; value: any; leafItem?: AnyItem };

/**
 * The display template to render a collection's rows loaded through `read` with: the one configured on
 * the collection, or {@link getCollectionDefaultDisplayTemplate} when it is unset, cleared, or references
 * only fields `read` does not return ({@link resolveReadOutput}), which would render nothing.
 */
export function resolveCollectionDisplayTemplate(collection: ResolvedMonospaceCollection, read: CollectionRead, schema: QuerySchemaWalker | null | undefined): DisplayTemplateAst | null {
	const configured = collection.meta?.displayTemplate;
	const output = resolveReadOutput(collection, read, schema);

	if (isEmptyDisplayTemplate(configured) || (output && !rendersReturnedField(configured, collection, output))) {
		return defaultDisplayTemplate(collection, output);
	}

	return configured;
}

/**
 * The display template of a collection without a configured one: its first string field, or else its
 * first primary-key field. Where `read` gates the collection's fields, only fields it returns qualify.
 */
export function getCollectionDefaultDisplayTemplate(collection: ResolvedMonospaceCollection, read: CollectionRead, schema: QuerySchemaWalker | null | undefined): DisplayTemplateAst | null {
	return defaultDisplayTemplate(collection, resolveReadOutput(collection, read, schema));
}

function defaultDisplayTemplate(collection: ResolvedMonospaceCollection, output: QuerySchemaOutput | undefined): DisplayTemplateAst | null {
	function returns(field: ResolvedMonospacePrimitiveField) {
		return !output || output.primitiveFieldById(field.id);
	}

	const titleField = collection.primitiveFields.find((field) => isPrimitiveTypeString(field.type) && returns(field))
		?? getCollectionPrimaryKeyFields(collection).find((field) => returns(field));

	return titleField ? fieldIdToDisplayTemplateAst(titleField.id) : null;
}

/** Whether a template renders any field `output` returns; one of static text alone renders regardless. */
function rendersReturnedField(template: DisplayTemplateAst, collection: ResolvedMonospaceCollection, output: QuerySchemaOutput): boolean {
	const refs = (template.content ?? []).filter((node): node is RefNode => node.type === DisplayTemplateAstType.Ref);
	return refs.length === 0 || refs.some((node) => outputReturnsFieldPath(output, collection, ensureArray(node.attrs.fieldId)));
}

/** The programmatic equivalent of typing `{{ fieldName }}` in the template editor. */
export function fieldIdToDisplayTemplateAst(fieldId: GlobalFieldId): DisplayTemplateAst {
	return fieldPathToDisplayTemplateAst([fieldId] as CollectionFieldPath);
}

/**
 * The default display template for an M2M relation: `{{ <junction-to-target field> }}`.
 *
 * Rendered against the JUNCTION row, so junction-local fields stay in scope for future
 * per-field templates; the default just surfaces the target. The target leg is field-relative
 * (it flips by viewing side), so this is derived from the resolved structure, never a static
 * junction-collection template.
 */
export function getM2MDefaultDisplayTemplate(structure: M2MStructure): DisplayTemplateAst {
	return fieldIdToDisplayTemplateAst(structure.junction.toTarget.id);
}

/** The programmatic equivalent of selecting a field path in the template editor. */
export function fieldPathToDisplayTemplateAst(fieldPath: CollectionFieldPath): DisplayTemplateAst {
	return {
		type: DisplayTemplateAstType.Doc,
		content: [fieldPathToDisplayTemplateRefNode(fieldPath)],
	};
}

export function fieldPathToDisplayTemplateRefNode(fieldPath: CollectionFieldPath): RefNode {
	return {
		type: DisplayTemplateAstType.Ref,
		attrs: {
			fieldId: fieldPath.length === 1 ? fieldPath[0]! : fieldPath,
		},
	};
}

export function isDisplayTemplateString(displayTemplate: DisplayTemplate): displayTemplate is string {
	return typeof displayTemplate === 'string';
}

export function isDisplayTemplateAst(displayTemplate: DisplayTemplate): displayTemplate is DisplayTemplateAst {
	return typeof displayTemplate === 'object' && 'type' in displayTemplate;
}

export function isEmptyDisplayTemplate(displayTemplate?: DisplayTemplate | null): displayTemplate is null | undefined {
	if (isNil(displayTemplate)) return true;
	if (isDisplayTemplateString(displayTemplate)) return displayTemplate.trim().length === 0;
	return isNullishOrEmpty(displayTemplate.content);
}

export function toDisplayTemplateAst(
	template: DisplayTemplate | null | undefined,
	collection: ResolvedMonospaceCollection,
): DisplayTemplateAst {
	const empty: DisplayTemplateAst = { type: DisplayTemplateAstType.Doc, content: [] };

	if (isNil(template)) return empty;
	if (isDisplayTemplateAst(template)) return template;
	if (typeof template !== 'string' || template.trim().length === 0) return empty;

	let parsed: ReturnType<typeof parseBlockExpression>;

	try {
		parsed = parseBlockExpression(template);
	}
	catch {
		return empty;
	}

	const content: DisplayTemplateAst['content'] = [];

	for (const node of parsed) {
		if (node.type === 'static') {
			const text = String(node.value);
			if (text.length > 0) content.push({ type: DisplayTemplateAstType.Text, text });
		}
		else if (node.type === 'expression') {
			const { child } = node;

			if (child.type === 'prop' && child.path.length > 0) {
				const fieldIds = resolveFieldPath(child.path, collection);

				if (fieldIds) {
					content.push({
						type: DisplayTemplateAstType.Ref,
						attrs: {
							fieldId: fieldIds.length === 1 ? fieldIds[0]! : fieldIds,
						},
					});
				}
			}
			else if (child.type === 'static' && child.value != null) {
				content.push({ type: DisplayTemplateAstType.Text, text: String(child.value) });
			}
		}
	}

	return { type: DisplayTemplateAstType.Doc, content };
}

function getTextNodeText(node: TextNode, index: number, nodeCount: number): string {
	let text = node.text;

	if (index === 0) {
		text = text.trimStart();
	}

	if (index === nodeCount - 1) {
		text = text.trimEnd();
	}

	return text;
}

function resolveFieldIdPath(fieldIds: GlobalFieldId[], parentField: AnyResolvedField): { apiPath: string[]; leafFieldId: GlobalFieldId } | null {
	const apiPath: string[] = [];
	let leafFieldId: GlobalFieldId | undefined;
	let currentCollection: ResolvedMonospaceCollection | undefined = isSingleRelationField(parentField) ? parentField.oppositeCollection : undefined;

	for (const id of fieldIds) {
		if (!currentCollection) return null;

		const field = findCollectionFieldById(currentCollection, id);
		if (!field) return null;

		apiPath.push(field.apiName);
		leafFieldId = field.id as GlobalFieldId;
		currentCollection = isSingleRelationField(field) ? field.oppositeCollection : undefined;
	}

	return leafFieldId ? { apiPath, leafFieldId } : null;
}

function createRefPlanNode(
	node: RefNode,
	collection: ResolvedMonospaceCollection,
): DisplayTemplateRenderRefPlanNode | null {
	const fieldIds = ensureArray(node.attrs.fieldId);
	const rootField = findCollectionFieldById(collection, fieldIds[0]!);

	if (!rootField) {
		console.warn(`[RenderDisplayTemplate]: Could not find field with id ${fieldIds[0]}.`);
		return null;
	}

	if (fieldIds.length <= 1) {
		return {
			type: DisplayTemplateAstType.Ref,
			fieldId: rootField.id as GlobalFieldId,
			valuePath: [rootField.apiName],
			leafItemPath: [],
		};
	}

	const nested = resolveFieldIdPath(fieldIds.slice(1), rootField);

	if (!nested) return null;

	return {
		type: DisplayTemplateAstType.Ref,
		fieldId: nested.leafFieldId,
		valuePath: [rootField.apiName, ...nested.apiPath],
		leafItemPath: [rootField.apiName, ...nested.apiPath.slice(0, -1)],
	};
}

export function createDisplayTemplateRenderPlan(
	template: DisplayTemplate | null | undefined,
	collection: ResolvedMonospaceCollection,
): DisplayTemplateRenderPlan {
	const ast = toDisplayTemplateAst(template, collection);
	const content = ast.content ?? [];
	const nodes: DisplayTemplateRenderPlanNode[] = [];

	for (const [nodeIndex, node] of content.entries()) {
		if (node.type === DisplayTemplateAstType.Text) {
			const text = getTextNodeText(node, nodeIndex, content.length);
			if (text) nodes.push({ type: DisplayTemplateAstType.Text, text });
			continue;
		}

		const refPlanNode = createRefPlanNode(node, collection);
		if (refPlanNode) nodes.push(refPlanNode);
	}

	return {
		collectionId: collection.id,
		nodes,
	};
}

export function resolveDisplayTemplateRenderPlan(
	plan: DisplayTemplateRenderPlan,
	item: AnyItem,
): ResolvedDisplayTemplateRenderNode[] {
	if (!item) return [];

	const nodes: ResolvedDisplayTemplateRenderNode[] = [];

	for (const node of plan.nodes) {
		if (node.type === DisplayTemplateAstType.Text) {
			nodes.push({ text: node.text });
			continue;
		}

		const leafItem = node.leafItemPath.length > 0 ? get(item, node.leafItemPath) : item;

		if (leafItem == null) {
			nodes.push({
				fieldId: node.fieldId,
				value: undefined,
				leafItem: undefined,
			});

			continue;
		}

		if (typeof leafItem !== 'object') {
			continue;
		}

		nodes.push({
			fieldId: node.fieldId,
			value: get(item, node.valuePath),
			leafItem,
		});
	}

	return nodes;
}

function resolveFieldPath(path: string[], collection: ResolvedMonospaceCollection): GlobalFieldId[] | null {
	if (path.length === 0) return null;

	const [segment, ...rest] = path;
	const allFields = [...collection.primitiveFields, ...collection.relationFields];
	const field = allFields.find((f) => f.id === segment || f.apiName === segment);

	if (!field) return null;

	if (rest.length === 0) return [field.id as GlobalFieldId];

	const relationField = collection.relationFields.find((f) => f.id === field.id);
	if (!relationField?.oppositeCollection) return null;

	const nested = resolveFieldPath(rest, relationField.oppositeCollection as ResolvedMonospaceCollection);
	if (!nested) return null;

	return [field.id as GlobalFieldId, ...nested];
}
